import { ChangeDetectorRef, Component, Input, NgZone, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { MatDialog } from '@angular/material/dialog';
import { Ability, subject } from '@casl/ability';
import { Subject, Subscription, timer } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ConfirmDialogComponent, ConfirmDialogModel } from 'src/app/shared/components';
import { CommonService } from 'src/app/shared/services/common.service';
import { ProjectsService } from 'src/app/modules/projects/core/projects.service';
import {
  ARCGIS_LAYER_URL_PATTERN,
  ArcgisLayerCheck,
  ArcgisLayerKind,
  ArcgisLayerSource,
  ArcgisPreflight,
  ArcgisPushFailure,
  ArcgisPushStatus,
  ArcgisPushTargetInput,
  ArcgisPushTrigger,
} from 'src/app/shared/models/arcgis-push';

// Pushes usually finish within seconds; poll only while some are pending.
const POLL_INTERVAL_MS = 15000;

type LayerControl = 'point_layer_url' | 'line_layer_url' | 'polygon_layer_url';
type StatusTone = 'success' | 'error' | 'progress' | 'default';

interface LayerField {
  kind: ArcgisLayerKind;
  control: LayerControl;
  label: string;
  hint: string;
}

@Component({
    selector: 'app-project-arcgis-push',
    templateUrl: './arcgis-push.component.html',
    styleUrls: ['./arcgis-push.component.scss'],
    standalone: false
})
export class ArcgisPushComponent implements OnInit, OnChanges, OnDestroy {
  @Input() projectId: string;

  readonly layerFields: LayerField[] = [
    { kind: 'point', control: 'point_layer_url', label: 'Points layer', hint: 'Points and volume captures' },
    { kind: 'line', control: 'line_layer_url', label: 'Lines layer', hint: 'Lines and length captures' },
    { kind: 'polygon', control: 'polygon_layer_url', label: 'Areas layer', hint: 'Polygons and area captures' },
  ];

  readonly form = new FormGroup({
    enabled: new FormControl<boolean>(false),
    trigger: new FormControl<ArcgisPushTrigger>('submit'),
    layer_source: new FormControl<ArcgisLayerSource>('project'),
    point_layer_url: new FormControl<string>('', Validators.pattern(ARCGIS_LAYER_URL_PATTERN)),
    line_layer_url: new FormControl<string>('', Validators.pattern(ARCGIS_LAYER_URL_PATTERN)),
    polygon_layer_url: new FormControl<string>('', Validators.pattern(ARCGIS_LAYER_URL_PATTERN)),
  });

  canEdit = false;
  loading = false;
  saving = false;
  testing = false;
  retrying = false;
  backfilling = false;
  status: ArcgisPushStatus | null = null;
  preflight: ArcgisPreflight | null = null;

  private pollSub: Subscription | null = null;
  private unsubscribeAbility: (() => void) | null = null;
  private readonly onDestroy: Subject<void> = new Subject<void>();

  constructor(
    private ability: Ability,
    private dialog: MatDialog,
    private zone: NgZone,
    private cdRef: ChangeDetectorRef,
    private commonService: CommonService,
    private projectsService: ProjectsService,
  ) {}

  ngOnInit(): void {
    this.refreshAccess();
    // CASL rules can finish syncing after this component initializes; re-check
    // on the ability's change stream like project-archive.component.ts.
    this.unsubscribeAbility = this.ability.on('updated', () => {
      const couldEdit = this.canEdit;
      this.refreshAccess();
      if (this.canEdit && !couldEdit && !this.status && !this.loading) {
        this.load(true);
      }
      this.cdRef.detectChanges();
    });
    if (this.canEdit) {
      this.load(true);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Angular reuses this component across same-route project switches, so a
    // projectId re-bind must drop the previous project's state.
    if (!changes.projectId || changes.projectId.firstChange) {
      return;
    }
    this.stopPolling();
    this.status = null;
    this.preflight = null;
    this.form.reset({
      enabled: false, trigger: 'submit', layer_source: 'project', point_layer_url: '', line_layer_url: '', polygon_layer_url: '',
    });
    this.refreshAccess();
    if (this.canEdit) {
      this.load(true);
    }
  }

  ngOnDestroy(): void {
    this.stopPolling();
    this.unsubscribeAbility?.();
    this.onDestroy.next();
    this.onDestroy.complete();
  }

  get connectionReady(): boolean {
    return this.status?.connection?.configured === true;
  }

  get usesOrgLayers(): boolean {
    return this.form.controls.layer_source.value === 'org';
  }

  // The org has default layers, or this project already points at them.
  get orgLayersOffered(): boolean {
    return this.orgHasLayers || this.status?.target?.layer_source === 'org';
  }

  get orgHasLayers(): boolean {
    return this.layerFields.some(field => !!this.status?.connection?.[field.control]);
  }

  get hasLayerUrl(): boolean {
    return this.layerFields.some(field => !!this.layerUrl(field.control));
  }

  // The URL a layer field pushes to: the org's in org mode, else the field's.
  layerUrl(control: LayerControl): string {
    return this.normalizeUrl(this.usesOrgLayers ? this.status?.connection?.[control] : this.form.controls[control].value);
  }

  get statusView(): { label: string; tone: StatusTone; detail: string | null } {
    const target = this.status?.target;
    if (!target) {
      return { label: 'Not set up', tone: 'default', detail: null };
    }
    if (!target.enabled) {
      return { label: 'Off', tone: 'default', detail: null };
    }
    if (target.status === 'auth_failed') {
      return { label: 'Paused: ArcGIS sign-in failed', tone: 'error', detail: target.status_message };
    }
    if (target.status === 'schema_invalid') {
      return { label: 'Paused: layers no longer match', tone: 'error', detail: target.status_message };
    }
    if (this.status.pending > 0) {
      return { label: 'Pushing', tone: 'progress', detail: null };
    }
    return { label: 'Active', tone: 'success', detail: null };
  }

  // How the saved trigger reads in the backfill confirmation.
  get backfillScope(): string {
    const trigger = this.status?.target?.trigger;
    if (trigger === 'save') {
      return 'every report on this project, drafts included,';
    }
    return `every ${trigger === 'approve' ? 'approved' : 'submitted'} report on this project`;
  }

  get removalHint(): string {
    return this.form.controls.trigger.value === 'save'
      ? 'When on, reports are sent as they are saved. Deleted reports are removed from the layers.'
      : 'When on, reports are sent automatically. Withdrawn or deleted reports are removed from the layers.';
  }

  urlError(control: LayerControl): string {
    const ctrl = this.form.controls[control];
    return ctrl.invalid && (ctrl.dirty || ctrl.touched)
      ? 'Use the layer URL ending in /FeatureServer/<layer number>'
      : '';
  }

  /**
   * The verdict for one layer field: a Test layers / Save result for the URL
   * now in the field, else the saved state (a saved target with status ok
   * passed its checks when saved and on every push since). Editing the URL
   * drops both until it is checked again.
   */
  layerCheck(kind: ArcgisLayerKind): (ArcgisLayerCheck & { saved?: boolean }) | null {
    const field = this.layerFields.find(f => f.kind === kind);
    const current = this.layerUrl(field.control);
    if (!current) {
      return null;
    }
    const live = this.preflight?.layers?.[kind];
    if (live && this.normalizeUrl(live.url) === current) {
      return live;
    }
    const target = this.status?.target;
    const savedUrl = target?.layer_source === 'org' ? this.status?.connection?.[field.control] : target?.[field.control];
    if (target?.enabled && target.status === 'ok'
      && (target.layer_source || 'project') === this.form.controls.layer_source.value
      && this.normalizeUrl(savedUrl) === current) {
      return { url: current, ok: true, errors: [], warnings: [], saved: true };
    }
    return null;
  }

  readyTitle(kind: ArcgisLayerKind): string {
    const check = this.layerCheck(kind);
    const checkedAt = this.status?.target?.checked_at;
    if (check?.saved && checkedAt) {
      return `Last checked ${new Date(checkedAt).toLocaleString()}`;
    }
    return 'Checked just now';
  }

  private normalizeUrl(url: string | null | undefined): string {
    return (url || '').trim().replace(/\/+$/, '');
  }

  failureLink(failure: ArcgisPushFailure): string[] | null {
    return failure.site_id ? ['/projects', this.projectId, 'sites', failure.site_id] : null;
  }

  failureQueryParams(failure: ArcgisPushFailure) {
    return { tab: 'daily-report', date: failure.report_date, reportId: failure.daily_report_id };
  }

  testLayers(): void {
    if (this.form.invalid || !this.hasLayerUrl) {
      this.form.markAllAsTouched();
      return;
    }
    this.testing = true;
    this.preflight = null;
    const urls = {
      point_layer_url: this.layerUrl('point_layer_url') || null,
      line_layer_url: this.layerUrl('line_layer_url') || null,
      polygon_layer_url: this.layerUrl('polygon_layer_url') || null,
    };
    this.projectsService.testArcgisPush(this.projectId, urls)
      .pipe(takeUntil(this.onDestroy))
      .subscribe({
        next: (preflight: ArcgisPreflight) => {
          this.testing = false;
          this.preflight = preflight;
          this.notifyPreflight(preflight);
        },
        error: (error: HttpErrorResponse) => {
          this.testing = false;
          this.commonService.notification(this.errorText(error, 'Could not check the layers'), 'danger');
        },
      });
  }

  save(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    this.saving = true;
    this.projectsService.saveArcgisPush(this.projectId, this.payload())
      .pipe(takeUntil(this.onDestroy))
      .subscribe({
        next: (resp) => {
          this.saving = false;
          this.preflight = resp?.preflight || null;
          this.commonService.notification(
            resp?.target?.enabled ? 'ArcGIS push is on' : 'ArcGIS push settings saved',
            'success',
          );
          this.load(true);
        },
        error: (error: HttpErrorResponse) => {
          this.saving = false;
          // A failed preflight comes back as 422 with the per-layer verdict.
          if (error?.status === 422 && error.error?.preflight) {
            this.preflight = error.error.preflight;
            this.commonService.notification('The layers are not ready yet. See the checks below.', 'danger');
            return;
          }
          this.commonService.notification(this.errorText(error, 'Could not save the ArcGIS push settings'), 'danger');
        },
      });
  }

  retry(): void {
    this.retrying = true;
    this.projectsService.retryArcgisPush(this.projectId)
      .pipe(takeUntil(this.onDestroy))
      .subscribe({
        next: (resp) => {
          this.retrying = false;
          this.commonService.notification(`${resp?.queued || 0} report(s) queued to push again`, 'success');
          this.load();
        },
        error: (error: HttpErrorResponse) => {
          this.retrying = false;
          this.commonService.notification(this.errorText(error, 'Could not retry the failed pushes'), 'danger');
        },
      });
  }

  backfill(): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      disableClose: true,
      data: new ConfirmDialogModel(
        'Push existing reports',
        `This sends ${this.backfillScope} to the ArcGIS layers, including reports from before the push was turned on.`,
        'Push reports',
        'Cancel',
      ),
    });
    dialogRef.afterClosed().pipe(takeUntil(this.onDestroy)).subscribe(confirmed => {
      if (!confirmed) {
        return;
      }
      this.backfilling = true;
      this.projectsService.backfillArcgisPush(this.projectId)
        .pipe(takeUntil(this.onDestroy))
        .subscribe({
          next: (resp) => {
            this.backfilling = false;
            this.commonService.notification(`${resp?.queued || 0} report(s) queued`, 'success');
            this.load();
          },
          error: (error: HttpErrorResponse) => {
            this.backfilling = false;
            this.commonService.notification(this.errorText(error, 'Could not queue the reports'), 'danger');
          },
        });
    });
  }

  // The per-layer verdicts sit under each URL field; this says the outcome at once.
  private notifyPreflight(preflight: ArcgisPreflight): void {
    const checks = Object.values(preflight?.layers || {});
    if (!checks.length) {
      this.commonService.notification(preflight?.message || 'Could not check the layers', 'danger');
      return;
    }
    const failing = checks.filter(check => !check.ok).length;
    if (!failing) {
      this.commonService.notification(`All ${checks.length} layer(s) are ready`, 'success');
    } else {
      this.commonService.notification(
        `${failing} of ${checks.length} layer(s) have a problem. See the checks under each URL.`,
        'danger',
      );
    }
  }

  private refreshAccess(): void {
    this.canEdit = !!this.projectId
      && this.ability.can('update', subject('Project', { id: this.projectId, uuid: this.projectId }));
  }

  /**
   * @param resetForm copy the saved settings into the form (first load and
   *   after a save); polling leaves what the user is typing alone
   */
  private load(resetForm = false): void {
    if (resetForm) {
      this.loading = true;
    }
    this.projectsService.getArcgisPush(this.projectId)
      .pipe(takeUntil(this.onDestroy))
      .subscribe({
        next: (status: ArcgisPushStatus) => {
          this.loading = false;
          this.status = status;
          if (resetForm) {
            const target = status?.target;
            // A new target starts on the org layers when the org has them;
            // an existing one keeps what it was saved with.
            const orgHas = this.layerFields.some(field => !!status?.connection?.[field.control]);
            this.form.reset({
              enabled: !!target?.enabled,
              trigger: target?.trigger || 'submit',
              layer_source: target ? (target.layer_source || 'project') : (orgHas ? 'org' : 'project'),
              point_layer_url: target?.point_layer_url || '',
              line_layer_url: target?.line_layer_url || '',
              polygon_layer_url: target?.polygon_layer_url || '',
            });
          }
          if (status?.pending > 0) {
            this.startPolling();
          } else {
            this.stopPolling();
          }
        },
        error: (error: HttpErrorResponse) => {
          this.loading = false;
          this.commonService.notification(this.errorText(error, 'Could not load the ArcGIS push settings'), 'danger');
        },
      });
  }

  private payload(): ArcgisPushTargetInput {
    const value = this.form.getRawValue();
    const url = (v: string | null) => (v || '').trim() || null;
    return {
      enabled: !!value.enabled,
      trigger: value.trigger || 'submit',
      layer_source: value.layer_source || 'project',
      point_layer_url: url(value.point_layer_url),
      line_layer_url: url(value.line_layer_url),
      polygon_layer_url: url(value.polygon_layer_url),
    };
  }

  private errorText(error: HttpErrorResponse, fallback: string): string {
    return error?.error?.error || error?.error?.message || fallback;
  }

  private startPolling(): void {
    if (this.pollSub) {
      return;
    }
    this.zone.runOutsideAngular(() => {
      this.pollSub = timer(POLL_INTERVAL_MS, POLL_INTERVAL_MS)
        .pipe(takeUntil(this.onDestroy))
        .subscribe(() => this.zone.run(() => this.load()));
    });
  }

  private stopPolling(): void {
    this.pollSub?.unsubscribe();
    this.pollSub = null;
  }
}
